'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { getCurrentUser } from '@/lib/auth/currentUser';
import { fetchTokenUser } from '@/lib/auth/accessToken';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import ProfileFieldInput from '@/components/registration/ProfileFieldInput';
import QuestionFields from '@/components/registration/QuestionFields';
import type { SpecialtiesValue } from '@/components/forms/SpecialtiesField';
import {
  registrationErrorKey,
  saveRegistrationProfile,
  type RegistrationChannel,
  type RegistrationNeeds,
} from '@/lib/registration/client';
import { addCompanions, initialShownFields, liveFields, profileUpdateFor, validateProfile } from '@/lib/registration/formModel';
import { uploadTicketDocument } from '@/lib/tickets/uploadDocument';
import {
  isWithinTicketDocumentSize,
  sortQuestions,
  validateAnswers,
  type QuestionAnswer,
} from '@/lib/tickets/questions';
import type { SpecialtyOption } from '@/lib/specialties/types';

export interface RegistrationFormProps {
  channel: RegistrationChannel;
  contentId: string;
  ticketId: string | null;
  needs: RegistrationNeeds;
  specialties: SpecialtyOption[];
  /** Widget token mode. */
  authToken?: string | null;
  /** messageKey: a failure about the whole form (e.g. a dropped connection), shown above the submit. */
  serverErrors?: { missingFields?: string[]; answerErrors?: Record<string, string>; messageKey?: string } | null;
  submitLabel: string;
  /** The parent is posting /api/registrations. */
  busy?: boolean;
  onProfileSaved?: () => void | Promise<void>;
  onSubmit: (answers: QuestionAnswer[]) => void | Promise<void>;
  onCancel?: () => void;
  /** Replaces saveRegistrationProfile (e.g. an organizer filling in for a participant). */
  saveProfile?: (update: Record<string, string>) => Promise<{ ok: true } | { ok: false; errorKey: string }>;
  /** Leaves file-upload questions out of the form. */
  excludeFileQuestions?: boolean;
}

/**
 * Uploading a document needs a Supabase browser session, which the widget only
 * has in cookie mode. That is checked before the form is shown, so nobody fills
 * everything in only to fail at submit.
 */
type UploadSession = 'checking' | 'available' | 'unavailable';
type Phase = 'idle' | 'uploading' | 'saving' | 'submitting';

/**
 * The one participant form: the profile fields this registration still needs
 * and the ticket's questions, on one screen. Order on submit is fixed –
 * validate, upload, save the profile, then hand the answers to the parent.
 */
export default function RegistrationForm({
  channel,
  contentId,
  ticketId,
  needs,
  specialties,
  authToken,
  serverErrors,
  submitLabel,
  busy = false,
  onProfileSaved,
  onSubmit,
  onCancel,
  saveProfile,
  excludeFileQuestions = false,
}: RegistrationFormProps) {
  const { t, language } = useLanguage();
  const formRef = useRef<HTMLFormElement>(null);
  const [values, setValues] = useState<Record<string, string>>(needs.values);
  const [shown, setShown] = useState<Set<string>>(() => initialShownFields(needs, specialties));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File>>({});
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // question id → the file this form already uploaded for it, so a retry after
  // a server refusal does not upload the same document again.
  const uploaded = useRef<Record<string, { file: File; path: string }>>({});

  const questions = useMemo(
    () => sortQuestions(excludeFileQuestions ? needs.questions.filter(q => q.field_type !== 'file') : needs.questions),
    [needs.questions, excludeFileQuestions]
  );
  const hasFileQuestion = questions.some(q => q.field_type === 'file');
  const hasRequiredFileQuestion = questions.some(q => q.field_type === 'file' && q.is_required);
  const [uploadSession, setUploadSession] = useState<UploadSession>(
    channel === 'embed' && hasFileQuestion ? 'checking' : 'available'
  );

  useEffect(() => {
    if (uploadSession !== 'checking') return;
    let cancelled = false;
    // Embed pages have no server snapshot of the user: ask the token route.
    fetchTokenUser()
      .then(user => { if (!cancelled) setUploadSession(user ? 'available' : 'unavailable'); })
      .catch(() => { if (!cancelled) setUploadSession('unavailable'); });
    return () => { cancelled = true; };
    // Checked once, when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The server refused: mark what it named, keep everything typed and chosen.
  const [appliedServerErrors, setAppliedServerErrors] = useState<RegistrationFormProps['serverErrors']>(null);
  if (serverErrors !== appliedServerErrors) {
    setAppliedServerErrors(serverErrors);
    const missing = serverErrors?.missingFields ?? [];
    if (missing.length > 0) {
      setShown(prev => addCompanions(new Set([...prev, ...missing])));
      setFieldErrors(prev => ({
        ...prev,
        ...Object.fromEntries(missing.map(name => [name, 'additionalFields.validation.required'])),
      }));
    }
    const refusedAnswers = serverErrors?.answerErrors;
    if (refusedAnswers) setAnswerErrors(prev => ({ ...prev, ...refusedAnswers }));
    if (serverErrors?.messageKey) setFormError(t(serverErrors.messageKey));
  }

  const live = liveFields(values, needs, specialties, shown);
  const working = phase !== 'idle' || busy;
  const eventUrl = `${(process.env.NEXT_PUBLIC_APP_URL || 'https://confy.cc').replace(/\/+$/, '')}/e/${contentId}`;
  const site = channel === 'site';

  const clearFieldError = (name: string) =>
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

  const clearAnswerError = (questionId: string) =>
    setAnswerErrors(prev => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

  const handleChange = (name: string, value: string) => {
    setValues(prev => (name === 'country' ? { ...prev, country: value, region: '' } : { ...prev, [name]: value }));
    clearFieldError(name);
    if (name === 'country') clearFieldError('region');
  };

  const handleSpecialtiesChange = (value: SpecialtiesValue) => {
    setValues(prev => ({
      ...prev,
      specialty_id: value.primary || '',
      secondary_specialty_ids: JSON.stringify(value.secondary),
      specialty_other: value.other,
    }));
    clearFieldError('specialty_id');
  };

  const handleResponse = (questionId: string, value: string) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    clearAnswerError(questionId);
  };

  const handleFile = (questionId: string, file: File | null) => {
    // Name the limit here instead of letting the bucket refuse the file later.
    if (file && !isWithinTicketDocumentSize(file.size)) {
      setFiles(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setAnswerErrors(prev => ({ ...prev, [questionId]: 'ticketQuestions.errorFileTooLarge' }));
      return;
    }
    setFiles(prev => {
      const next = { ...prev };
      if (file) next[questionId] = file;
      else delete next[questionId];
      return next;
    });
    clearAnswerError(questionId);
  };

  const uploadFile = async (questionId: string, file: File): Promise<string> => {
    const done = uploaded.current[questionId];
    if (done && done.file === file) return done.path;

    const user = channel === 'embed' ? await fetchTokenUser() : getCurrentUser();
    if (!user) throw new Error(t('ticketCustomFields.failedToUploadFile', { error: t('ticketCustomFields.noActiveSession') }));
    // Questions only ever come from a ticket.
    if (!ticketId) throw new Error(t('ticketCustomFields.failedToSubmitForm'));

    let path: string;
    try {
      path = await uploadTicketDocument(createClient(), { userId: user.id, ticketId, questionId, file });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(t('ticketCustomFields.failedToUploadFile', { error: message }));
    }
    uploaded.current[questionId] = { file, path };
    return path;
  };

  const scrollToFirstError = (profileErrors: Record<string, string>, questionErrors: Record<string, string>) => {
    const firstField = live.find(({ field }) => profileErrors[field.field_name])?.field;
    const selector = firstField
      ? `[data-field-name="${firstField.field_name}"]`
      : (() => {
        const question = questions.find(q => questionErrors[q.id]);
        return question ? `[data-question-id="${question.id}"]` : null;
      })();
    if (!selector) return;
    formRef.current?.querySelector(selector)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (working) return;
    setFormError(null);

    // (1) Validate. A chosen file counts as answered before it is uploaded.
    const profileErrors = validateProfile(live, values);
    const questionErrors = validateAnswers(questions, questions.map(q => ({
      questionId: q.id,
      value: q.field_type === 'file' ? null : (responses[q.id] ?? null),
      fileUrl: q.field_type === 'file' && files[q.id] ? 'pending-upload' : null,
    })));
    setFieldErrors(profileErrors);
    setAnswerErrors(questionErrors);
    if (Object.keys(profileErrors).length > 0 || Object.keys(questionErrors).length > 0) {
      setFormError(t('additionalFields.validation.fillAllRequired'));
      scrollToFirstError(profileErrors, questionErrors);
      return;
    }

    try {
      // (2) Upload the chosen documents.
      setPhase('uploading');
      const fileUrls: Record<string, string> = {};
      try {
        for (const q of questions) {
          const file = files[q.id];
          if (q.field_type === 'file' && file) fileUrls[q.id] = await uploadFile(q.id, file);
        }
      } catch (error) {
        setFormError(error instanceof Error ? error.message : t('ticketCustomFields.failedToSubmitForm'));
        return;
      }

      // (3) Save the profile – a failure here stops the registration.
      setPhase('saving');
      const update = profileUpdateFor(live, values);
      const saved = saveProfile
        ? await saveProfile(update)
        : await saveRegistrationProfile({ channel, contentId, update, token: authToken });
      if (!saved.ok) {
        setFormError(t(saved.errorKey));
        return;
      }

      // The parent's callbacks should not throw; if one does, say so here
      // instead of leaving an unhandled rejection and a silent form.
      try {
        // (4) Let the parent refresh what it knows about the profile.
        await onProfileSaved?.();

        // (5) Hand over one answer per question.
        setPhase('submitting');
        await onSubmit(questions.map(q => {
          const raw = q.field_type === 'file' ? '' : (responses[q.id] ?? '').trim();
          return { questionId: q.id, value: raw || null, fileUrl: fileUrls[q.id] ?? null };
        }));
      } catch {
        setFormError(t(registrationErrorKey(null, channel)));
      }
    } finally {
      setPhase('idle');
    }
  };

  if (uploadSession === 'checking') {
    return (
      <div role="status" aria-label={t('common.loading')} className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-900 rounded-full" />
      </div>
    );
  }

  // A required document cannot be supplied here at all, so the registration is
  // handed over to the full event page instead of being started and abandoned.
  if (uploadSession === 'unavailable' && hasRequiredFileQuestion) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
        <h3 className="text-sm font-semibold text-amber-900">
          {t('embed.documentUploadTitle')}
        </h3>
        <p className="text-xs text-amber-900">
          {t('embed.documentUploadUnavailable')}
        </p>
        <a
          href={eventUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-xs font-medium text-blue-700 hover:underline"
        >
          {t('embed.completeOnConfy')}
        </a>
      </div>
    );
  }

  const headingClass = site ? 'text-lg font-semibold text-gray-900' : 'text-sm font-semibold text-gray-900';
  const label = phase === 'saving' ? t('additionalFields.saving') : submitLabel;

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate className={site ? 'space-y-6' : 'space-y-4'}>
      {live.length > 0 && (
        <section className={site ? 'space-y-6' : 'space-y-4'}>
          <h3 className={headingClass}>{t('registrationForm.aboutYou')}</h3>
          {live.map(({ field, required }) => (
            <ProfileFieldInput
              key={field.field_name}
              field={field}
              required={required}
              values={values}
              error={fieldErrors[field.field_name]}
              specialties={specialties}
              language={language}
              onChange={handleChange}
              onSpecialtiesChange={handleSpecialtiesChange}
            />
          ))}
        </section>
      )}

      {questions.length > 0 && (
        <section className={site ? 'space-y-6' : 'space-y-4'}>
          <h3 className={headingClass}>{t('registrationForm.questions')}</h3>
          <QuestionFields
            questions={questions}
            responses={responses}
            files={files}
            errors={answerErrors}
            uploadBlocked={uploadSession === 'unavailable'}
            eventUrl={eventUrl}
            onResponse={handleResponse}
            onFile={handleFile}
          />
        </section>
      )}

      {formError && (
        <p role="alert" className={site ? 'text-sm text-red-600' : 'text-xs text-red-600'}>{formError}</p>
      )}

      {site ? (
        // Stacked (submit on top) on a phone, where two long labels cannot share a row.
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          {onCancel && (
            <Button type="button" variant="secondary" className="w-full sm:w-auto sm:flex-1 sm:min-w-0 sm:shrink whitespace-normal h-auto min-h-11" onClick={onCancel} disabled={working}>
              {t('common.cancel')}
            </Button>
          )}
          {/* A long label (e.g. «Зберегти та змінити квиток») wraps inside the dialog instead of overflowing it. */}
          <Button type="submit" variant="primary" className="w-full sm:w-auto sm:flex-[2] sm:min-w-0 sm:shrink whitespace-normal h-auto min-h-11" disabled={working}>
            {label}
          </Button>
        </div>
      ) : (
        <button
          type="submit"
          disabled={working}
          className="w-full py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {label}
        </button>
      )}
    </form>
  );
}
